import { useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { type ChannelId } from '@onr/schema'
import { useLocation } from '@tanstack/react-router'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'

import { useFirebaseUser } from '@/auth/useFirebaseUser'
import { type ToastProps } from '@/components/atoms/Toast'
import { channelName } from '@/pages/messages/channelLabels'
import { useMutedChannels } from '@/state/channels/useChannelPreferences'

import { DashboardAlertContext } from './DashboardAlertContext'

export interface ToasterActions {
  onHighlight?: (requestId: string) => void
  onTeamMessageClick?: (teamId: string) => Promise<void>
  currentTeamId?: string
  onChannelMessageClick?: (channelId: ChannelId) => Promise<void>
}

export type DashboardAlertToastProps = ToastProps & {
  id: string
  title: string
  description: string
  actions: Array<{
    label: string
    onClick: () => Promise<void> | void
  }>
}

export const useDashboardAlertToasts = ({
  onHighlight,
  onTeamMessageClick,
  currentTeamId,
  onChannelMessageClick,
}: ToasterActions) => {
  const { alerts, dismissAlert } = useContext(DashboardAlertContext)
  const { firebaseUser } = useFirebaseUser() ?? {}
  const intl = useIntl()
  // Read from the route and the user document rather than from props: a channel alert has to be
  // silenced the same way on every screen that mounts a Toaster.
  const openChannelId = useLocation({ select: (location) => location.pathname.split('/messages/channels/')[1] })
  const isChannelMuted = useMutedChannels()

  // A muted channel's toast never opens, so its `onOpenChange` never fires: without this, unmuting
  // would pop every alert the channel accumulated since app start at once.
  useEffect(() => {
    alerts
      .filter((alert) => alert.type === 'channelMessage' && !alert.isDismissed && isChannelMuted(alert.channelId))
      .forEach((alert) => {
        dismissAlert(alert._id)
      })
  }, [alerts, dismissAlert, isChannelMuted])

  const toasts = useMemo<DashboardAlertToastProps[]>(
    () =>
      alerts.map((alert) => {
        const { _id, isDismissed } = alert

        if (alert.type === 'transportRequest') {
          const { event, transportRequestId, transportRequestPiloteId } = alert
          return {
            id: _id,
            title: intl.formatMessage(
              { id: `dashboardAlert.title.${event}` },
              { transportRequestId: transportRequestPiloteId }
            ),
            description: intl.formatMessage({ id: `dashboardAlert.description.${event}` }),
            open: !isDismissed,
            duration: 15 * 60 * 60 * 1000,
            onOpenChange: (open: boolean) => {
              if (!open) {
                dismissAlert(_id)
              }
            },
            actions: [
              {
                label: intl.formatMessage({ id: 'dashboardAlert.open' }),
                onClick: () => {
                  onHighlight?.(transportRequestId)
                  dismissAlert(_id)
                },
              },
              {
                label: intl.formatMessage({ id: 'dashboardAlert.dismiss' }),
                onClick: () => {
                  dismissAlert(_id)
                },
              },
            ],
          }
        }
        if (alert.type === 'teamMessage') {
          const { teamId, teamNumber, hasText, hasPicture, hasAudio } = alert

          let description: string
          if (hasAudio) {
            description = intl.formatMessage({ id: 'dashboardAlert.description.teamMessage.audio' })
          } else if (hasText && hasPicture) {
            description = intl.formatMessage({ id: 'dashboardAlert.description.teamMessage.textAndPicture' })
          } else if (hasPicture) {
            description = intl.formatMessage({ id: 'dashboardAlert.description.teamMessage.picture' })
          } else if (hasText) {
            description = intl.formatMessage({ id: 'dashboardAlert.description.teamMessage.text' })
          } else {
            description = intl.formatMessage({ id: 'dashboardAlert.description.teamMessage' })
          }

          const isCurrentTeam = currentTeamId === teamId
          const open = !isDismissed && !isCurrentTeam

          return {
            id: _id,
            title: intl.formatMessage({ id: 'dashboardAlert.title.teamMessage' }, { teamNumber }),
            description,
            open,
            duration: 15 * 60 * 60 * 1000,
            variant: 'teamMessage' as const,
            onOpenChange: (isOpen: boolean) => {
              if (!isOpen) {
                dismissAlert(_id)
              }
            },
            actions: [
              {
                label: intl.formatMessage({ id: 'dashboardAlert.viewMessage' }),
                onClick: () => {
                  if (onTeamMessageClick) {
                    handleNotAwaitedPromise(onTeamMessageClick(teamId))
                  }
                  dismissAlert(_id)
                },
              },
              {
                label: intl.formatMessage({ id: 'dashboardAlert.dismiss' }),
                onClick: () => {
                  dismissAlert(_id)
                },
              },
            ],
          }
        }

        if (alert.type === 'channelMessage') {
          const { channelId, sender, senderId, senderName, teamNumber, textExcerpt } = alert

          const isOwnMessage = senderId === firebaseUser?.uid
          const open = !isDismissed && !isOwnMessage && openChannelId !== channelId && !isChannelMuted(channelId)
          const name = channelName(intl, channelId)

          return {
            id: _id,
            title:
              sender === 'volunteer'
                ? intl.formatMessage(
                    { id: 'dashboardAlert.title.channelMessage.team' },
                    { channelName: name, teamNumber: teamNumber ?? '?', name: senderName }
                  )
                : intl.formatMessage(
                    { id: 'dashboardAlert.title.channelMessage.central' },
                    { channelName: name, name: senderName }
                  ),
            // The trigger empties the excerpt when a super admin deletes the message.
            description: textExcerpt || intl.formatMessage({ id: 'channel.message.deleted' }),
            open,
            duration: 15 * 60 * 60 * 1000,
            variant: 'teamMessage' as const,
            onOpenChange: (isOpen: boolean) => {
              if (!isOpen) {
                dismissAlert(_id)
              }
            },
            actions: [
              {
                label: intl.formatMessage({ id: 'dashboardAlert.viewMessage' }),
                onClick: () => {
                  if (onChannelMessageClick) {
                    handleNotAwaitedPromise(onChannelMessageClick(channelId))
                  }
                  dismissAlert(_id)
                },
              },
              {
                label: intl.formatMessage({ id: 'dashboardAlert.dismiss' }),
                onClick: () => {
                  dismissAlert(_id)
                },
              },
            ],
          }
        }

        alert satisfies never

        return {
          id: _id,
          title: 'Unknown alert',
          description: 'Unknown alert type',
          open: !isDismissed,
          duration: 15 * 60 * 60 * 1000,
          onOpenChange: (open: boolean) => {
            if (!open) {
              dismissAlert(_id)
            }
          },
          actions: [],
        }
      }),
    [
      alerts,
      dismissAlert,
      intl,
      onHighlight,
      onTeamMessageClick,
      currentTeamId,
      onChannelMessageClick,
      openChannelId,
      isChannelMuted,
      firebaseUser?.uid,
    ]
  )

  return { toasts }
}
